<template>
  <BaseModal
    title="좌석선택"
    :modalState="busSeatModal"
    @closeBusSeat="$emit('closeBusSeat')"
  >
    <template #modalContent>
      <div class="seat">
        <select @change="changeSelect($event.target.value)">
          <option
            v-for="(allocInfo, index) in allocList"
            :key="index"
            :value="allocInfo.uid"
            :selected="selectedAllocUid === allocInfo.uid"
          >
            {{ allocInfo.displayText }}
          </option>
        </select>
        <div class="seat_guide">
          <div class="abled">신청가능</div>
          <div class="selected">선택좌석</div>
          <div class="disabled">예약완료</div>
        </div>
        <div class="seat_standard">
          <em>운전석</em>
          <em>출입문</em>
        </div>
        <div
          v-for="(floor, floorIdx) in layout"
          :key="`floor${floorIdx}`"
          class="seat_layout"
        >
          <div
            v-for="(row, rowIdx) in floor"
            :key="`row${rowIdx}`"
            class="seat_row"
          >
            <div
              v-for="(column, columnIdx) in row"
              :key="`column${columnIdx}`"
              class="seat_area"
            >
              <button
                v-if="column.seatNo"
                @click.prevent="selectSeat(column.seatNo)"
                :class="setClassName(column)"
                :disabled="setClassName(column) === 'disabled'"
                class="seat"
              >
                <span>
                  {{ column.seatNo }}
                </span>
              </button>
              <div v-if="!column.seatNo && rowIdx > 2" class="arrow">
                <img src="@/assets/img/icon/direction.svg" alt="" />
              </div>
            </div>
          </div>
        </div>
        <div class="button_area bottom">
          <div
            class="guide"
            :class="{
              hide: totalSeatCount === maxSeatCount,
            }"
          >
            <!--좌석 선택 완료시 hide 클래스 추가-->
            좌석을 선택해주세요 (<span
              >{{ totalSeatCount }}/{{ maxSeatCount }}</span
            >)
          </div>
          <!-- allocInfo.remainCount 처리 -->
          <button
            class="btnB"
            :class="{
              active: isActive,
            }"
            :disabled="!isActive"
            @click="closeBusSeat"
          >
            좌석선택완료
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { ref, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import BaseModal from "@/components/Base/BaseModal.vue";

export default {
  name: "BusSeat",
  props: {
    busSeatModal: {
      type: Boolean,
    },
    allocList: {
      type: Array,
    },
    lineInfo: {
      type: Object,
    },
    dateIdx: {
      type: Number,
    },
  },
  components: {
    BaseModal,
  },
  setup(props, { emit }) {
    const store = useStore();

    // 선택된 호차 정보
    const selectedAllocUid = ref("");

    const layout = ref([]);
    // 선택한 호차 좌석 배열
    const allocSeats = ref([]);
    const maxSeatCount = computed(() => props.lineInfo.targetList.length);
    const totalSeatCount = computed(() => allocSeats.value.length);
    const isActive = computed(
      () => totalSeatCount.value === maxSeatCount.value
    );

    // 좌석배치도 조회
    const getLayout = async () => {
      const result = await store.dispatch("book/FETCH_BUS_LAYOUT", {
        lineTurnUid: props.lineInfo.lineTurnUid,
        date: props.lineInfo.date,
        allocUid: selectedAllocUid.value,
      });
      layout.value = result.seats;
    };

    const changeSelect = (allocUid) => {
      selectedAllocUid.value = allocUid;
      getLayout();
    };

    const selectSeat = (seatNo) => {
      if (isSelectedSeat(seatNo)) {
        allocSeats.value = allocSeats.value.filter(
          (seatInfo) =>
            !(
              seatInfo.seatNo === seatNo &&
              seatInfo.allocUid === selectedAllocUid.value
            )
        );
        return;
      }
      if (totalSeatCount.value >= maxSeatCount.value) {
        return;
      }
      allocSeats.value.push({
        allocUid: selectedAllocUid.value,
        seatNo: seatNo,
      });
    };

    const isSelectedSeat = (seatNo) => {
      return !!allocSeats.value.find(
        (seatInfo) =>
          seatInfo.seatNo === seatNo &&
          seatInfo.allocUid === selectedAllocUid.value
      );
    };

    const setClassName = (seat) => {
      switch (true) {
        case isSelectedSeat(seat.seatNo): {
          // 좌석 선택
          return "active";
        }
        case seat.bookedYn: {
          // 이미 예약된 좌석
          return "disabled";
        }
        case totalSeatCount.value === maxSeatCount.value: {
          // 좌석 선택 완료시 선택 가능하지만 선택하지 않은 나머지
          return "selected";
        }
      }
    };

    const closeBusSeat = () => {
      allocSeats.value.sort((a, b) => {
        // allocUid 정렬
        if (a.allocUid > b.allocUid) {
          return 1;
        } else if (a.allocUid < b.allocUid) {
          return -1;
        }
        // seatNo 정렬
        if (a.seatNo > b.seatNo) {
          return 1;
        } else if (a.seatNo < b.seatNo) {
          return -1;
        }
        return 0;
      });
      const seatList = allocSeats.value.map((seat) => seat.seatNo);
      const busOrder = props.allocList.find(
        (allocInfo) => allocInfo.uid === selectedAllocUid.value
      )?.busOrder;
      emit("closeBusSeat", {
        dateIdx: props.dateIdx,
        seats: allocSeats.value,
        busOrder,
        seatList,
      });
    };

    onBeforeMount(() => {
      if (props.lineInfo?.seats.length) {
        allocSeats.value = [...props.lineInfo.seats];
        selectedAllocUid.value = props.lineInfo.seats[0].allocUid;
      } else {
        selectedAllocUid.value = props.allocList[0].uid;
      }
      getLayout();
    });

    return {
      layout,
      allocSeats,
      maxSeatCount,
      isActive,
      totalSeatCount,
      selectedAllocUid,
      selectSeat,
      setClassName,
      closeBusSeat,
      changeSelect,
    };
  },
};
</script>
